import React, {
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getMeasureInWindow } from '../common/utils';
import './index.css';

const defaultFrame = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};

export enum DirectionEnum {
  row = 'row', // 默认
  column = 'column',
}

const getDirectionByRef = (ref) => {
  if (!ref) return DirectionEnum.row;
  const computedStyle = window.getComputedStyle(ref);
  return computedStyle.getPropertyValue('flex-direction') as DirectionEnum;
};

function getDirectionStyle(direc) {
  const overFlowStyle = {
  };
  if (!direc) {
    return overFlowStyle;
  }
  if (direc === 'horizontal' || direc === DirectionEnum.row) {
    return Object.assign(overFlowStyle, {
      flexDirection: 'row',
      overflowX: 'auto',
      overflowY: 'hidden',
    });
  }
  if (direc === DirectionEnum.column) {
    return {
      height: '100%', // 默认，但是row时不应设置
      flexWrap: 'nowrap', // 子元素高度100%时，会横向摊开，导致样式错乱。
      // alignItems: 'center', // column布局是会有问题
    };
  }
  // else {
  //   Object.assign(overFlowStyle, {
  //     // flexDirection: 'column',
  //     overflowY: 'auto',
  //     overflowX: 'hidden',
  //   });
  // }
  return overFlowStyle;
}

const useIsMovingHooks = () => {
  const isScrollingRef = useRef<boolean | null>(null);
  const [isScrolling, setIsScrolling] = useState<boolean | null>(null);
  const isTouchingRef = useRef<boolean | null>(null);
  const [isTouching, setIsTouching] = useState<boolean | null>(null);

  const changScrolling = (isScrollingi: boolean) => {
    if (isScrollingRef.current === isScrollingi) return;
    isScrollingRef.current = isScrollingi;
    setIsScrolling(isScrollingi);
  };

  const changeTouching = (isTouchingi: boolean) => {
    if (isTouchingRef.current === isTouchingi) return;
    isTouchingRef.current = isTouchingi;
    setIsTouching(isTouchingi);
  };

  return {
    isScrollingRef,
    isScrolling,
    isTouching,
    isTouchingRef,
    changScrolling,
    changeTouching,
  };
};

export default forwardRef(
  (
    props: PropsWithChildren<{
      // 滑动方向由 flex-direction 指定
      direction?: string;
      className?: string;
      style?: CSSProperties;
      showScrollbar?: boolean;
      // machpro设计为默认展示，大部分效果是默认不展示
      showScrollIndicator?: boolean; // https://km.sankuai.com/collabpage/1204994826
      offset?: number;
      onClick?: (e?: any) => void;
      onScroll?: (e?: any) => void;
      onScrollStart?: (e?: any) => void;
      onScrollEnd?: (e?: any) => void;
      onLayout?: (event?: any) => void;
      pagingEnabled?: boolean;
    }>,
    ref
  ) => {
    const {
      direction,
      className,
      onClick,
      onScroll,
      onScrollEnd,
      onScrollStart,
      offset,
      showScrollbar = true,
      style,
      showScrollIndicator = true,
      onLayout,
      pagingEnabled = false,
    } = props;
    const {
      isScrolling, isTouching, changeTouching, changScrolling
    } = useIsMovingHooks();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const onLayoutRef = useRef(onLayout);
    onLayoutRef.current = onLayout;
    const hasOnLayout = typeof onLayout === 'function';
    const startScrollTimerRef = useRef(undefined as number | undefined);
    const [styleDirection, setStyleDirection] = useState<DirectionEnum | null>(null);
    // 自动调整样式，优先级：props.direction > stype.flex-direction > 组件内部计算 > 默认row
    // 逻辑： 1. 优先使用 direction 属性 2. 其次使用 flex-direction 属性
    const customStyle = useMemo(
      () => ({
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        ...getDirectionStyle(styleDirection), // 样式属性方向对应的样式
        ...(style || {}), // 外部props传入自定义样式。
        ...getDirectionStyle(direction), // props方向对应的样式
      }),
      [direction, styleDirection, style]
    );

    const getDirection = () => {
      if (direction) {
        return ['horizontal', 'row'].includes(direction) ? DirectionEnum.row : DirectionEnum.column;
      }
      const flexDirection = getDirectionByRef(scrollerRef.current);
      if (flexDirection) {
        return flexDirection === 'column' ? DirectionEnum.column : DirectionEnum.row;
      }
      return DirectionEnum.row;
    };

    const distCls = useMemo(() => {
      let _distCls = 'custom-mach-scroller';
      if (className) {
        _distCls = `${_distCls} ${className}`;
      }
      if (!showScrollbar || !showScrollIndicator) {
        _distCls = `${_distCls} custom-mach-scroller-hidden-bar`;
      }
      return _distCls;
    }, [className, showScrollIndicator, showScrollbar]);

    const contentOffset = () => {
      if (!scrollerRef.current) {
        return {
          x: 0,
          y: 0,
          scrollLeft: 0,
          scrollTop: 0,
          scrollWidth: 0,
          scrollHeight: 0,
        };
      }
      const scroller = scrollerRef.current || ({} as HTMLDivElement);
      const scrollLeft = scroller.scrollLeft;
      const scrollTop = scroller.scrollTop;
      const scrollWidth = scroller.scrollWidth;

      // 获取元素的边界矩形
      const rect = scroller.getBoundingClientRect();

      // 获取元素的宽度和高度
      const scrollHeight = rect.height;
      return {
        x: scrollLeft,
        y: scrollTop,
        scrollLeft,
        scrollTop,
        scrollWidth,
        scrollHeight,
      };
    };

    const addResizeObserver = (callbacks: Function[] = []) => {
      const element = scrollerRef.current;
      if (!element) {
        return undefined;
      }
      const handleResizeCb = () => {
        // 每次尺寸变化都重新测量，避免向 onLayout 传递首次注册时的旧尺寸。
        const detail = getMeasureInWindow(element);
        callbacks.forEach((fn) => {
          fn({
            detail,
          });
        });
      };
      const resizeObserver = new ResizeObserver(handleResizeCb);
      // 开始观察传入的元素
      resizeObserver.observe(element);
      handleResizeCb();
      return () => {
        // 使用注册时捕获的节点清理，避免卸载时 ref 已指向新节点或为空。
        resizeObserver.disconnect();
      };
    };

    const scrollToOffset = (scrollOffset: { x: number; y: number; }, animated: boolean) => {
      const behavior = animated ? 'smooth' : 'auto';
      scrollerRef.current?.scrollTo(
        styleDirection === DirectionEnum.row
          ? { left: scrollOffset.x, behavior }
          : { top: scrollOffset.y, behavior }
      );
    };

    const measure = () => {
      if (!scrollerRef.current) {
        return defaultFrame;
      }
      // 获取元素的边界矩形
      const rect = scrollerRef.current.getBoundingClientRect();

      // 获取父元素的边界矩形
      const parent = scrollerRef.current.parentElement;
      if (!parent) return defaultFrame;
      const parentRect = parent.getBoundingClientRect();

      // 计算相对父元素的坐标
      const x = rect.left - parentRect.left;
      const y = rect.top - parentRect.top;

      // 获取元素的宽度和高度
      const width = rect.width;
      const height = rect.height;
      // 返回结果
      return {
        x,
        y,
        width,
        height,
      };
    };

    const measureInWindow = () => {
      if (!scrollerRef.current) {
        return defaultFrame;
      }
      const rect = scrollerRef.current.getBoundingClientRect();

      return {
        y: rect.top + window.scrollY,
        x: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      };
    };

    // TODO 对外暴露方法
    // x: 横轴偏移量
    // y: 纵轴偏移量
    // scrollLeft：同 x
    // scrollTop：同 y
    // scrollWidth：content 宽度
    // scrollHeight：content 高度
    useImperativeHandle(ref, () => ({
      measure,
      measureInWindow,
      // 滚动到指定的偏移量
      scrollToOffset,
      // 获取偏移量，V7.91 起支持
      contentOffset,
      // 子线程获取偏移量，V8.19 起支持
      contentOffsetAsync: (callback) => {
        callback?.(contentOffset());
      },
      getBoundingClientRect: () => scrollerRef.current?.getBoundingClientRect(),
    }));

    useLayoutEffect(() => {
      if (!scrollerRef.current) return;
      function _loop(children, parent) {
        if (!children || !children.length) return;
        if (children.length === 1) {
          _loop(children[0].children, children[0]);
          return;
        }
        if (direction === 'horizontal') {
          parent.style.flexDirection = 'row';
        } else {
          // flex默认为column
          // parent.style.flexDirection = 'column';
        }
        [...children].forEach((item) => {
          item.style.flexShrink = 0;
        });
      }
      _loop(scrollerRef.current?.children, scrollerRef.current);
      setStyleDirection(getDirectionByRef(scrollerRef.current));
    }, [direction]);

    useEffect(() => {
      if (!scrollerRef.current) return;
      // console.log('sssss', getDirectionByRef(scrollerRef.current));
      setStyleDirection(getDirectionByRef(scrollerRef.current));
    }, []);

    useEffect(() => {
      if (!hasOnLayout || !scrollerRef.current) return;
      return addResizeObserver([(event) => onLayoutRef.current?.(event)]);
    }, [hasOnLayout]);

    function handleScroll(e) {
      clearTimeout(startScrollTimerRef.current);
      const targetDom = e?.target || {};
      const scrollHeight = targetDom?.scrollHeight;
      const scrollLeft = targetDom?.scrollLeft;
      const scrollWidth = targetDom?.scrollWidth;
      const scrollTop = targetDom?.scrollTop;
      const params = {
        scrollHeight,
        scrollLeft,
        scrollWidth,
        scrollTop,
      };
      changScrolling(true);
      onScroll && onScroll(params);

      startScrollTimerRef.current = setTimeout(() => {
        changScrolling(false);
        clearTimeout(startScrollTimerRef.current as number);
      }, 100);
    }

    function handleTouchStart() {
      changeTouching(true);
    }

    function handleTouchEnd() {
      changeTouching(false);
    }

    function handlePagingEnabled(params) {
      if (!pagingEnabled || !scrollerRef.current) return;
      const {
        clientWidth, clientHeight, scrollLeft, scrollTop
      } = params;
      const direction = getDirection();
      if (direction === DirectionEnum.row) {
        const idx = Math.round(scrollLeft / clientWidth);
        scrollToOffset({ x: idx * clientWidth, y: scrollTop }, true);
      } else {
        const idx = Math.round(scrollTop / clientHeight);
        scrollToOffset({ x: scrollLeft, y: idx * clientHeight }, true);
      }
    }

    useEffect(() => {
      if (!scrollerRef.current) return;
      if (isScrolling === false && isTouching === false) {
        const {
          clientWidth, clientHeight, scrollLeft, scrollTop
        } = scrollerRef.current;
        const params = {
          clientWidth,
          clientHeight,
          scrollLeft,
          scrollTop,
        };
        onScrollEnd && onScrollEnd(params);
        handlePagingEnabled(params);
      }
    }, [isScrolling, isTouching]);

    return (
      // @ts-ignore
      <div
        className={distCls}
        ref={scrollerRef}
        style={customStyle}
        onScroll={handleScroll}
        onClick={onClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {props.children}
      </div>
    );
  }
);
